import React, { useState } from 'react';
import { Divider, HStack, IconValidationValid, Stack, Text, VStack, } from '@adc/parallax-component-library';
import { PracticeErrorCode } from 'Enums';
import { useApiError, useLibrePractice, useToast } from 'Hooks';
import { Button, Footer, Form, Main, RichText, ScrollContainer, Spinner, TitleActionBar, } from 'Components/utility';
import { CLOSE_MODAL, SHOW_MODAL } from 'Reducers/modal/types';
import { applyPhoneNumberMask, getDocumentPathFromLanguage } from 'Utilities/dataHelpers';
import i18n from 'Utilities/i18n';
import mediator from 'Utilities/mediator';
import { store } from 'Utilities/store';
const PracticeInvitation = ({ data: { practiceInvitation } }) => {
    const [loading, setLoading] = useState(false);
    const { acceptOrRejectPracticeInvitation } = useLibrePractice();
    const { showApiErrorModal } = useApiError();
    const { showToast } = useToast();
    const onPrivacyNoticeLinkClick = () => {
        const { documentsUrl, documents } = store.getState().config;
        const { language } = store.getState().lang;
        const privacyNoticePath = getDocumentPathFromLanguage(documents?.privacypolicy, language);
        if (privacyNoticePath) {
            const privacyNoticeFullUrl = `${documentsUrl}${privacyNoticePath}`;
            window.open(privacyNoticeFullUrl, '_blank');
        }
    };
    const handleAccept = async () => {
        if (!practiceInvitation) {
            return;
        }
        setLoading(true);
        const resp = await acceptOrRejectPracticeInvitation(practiceInvitation.invitationId, 'accept');
        setLoading(false);
        if (!resp.errorCode && resp.practiceConnection) {
            showToast({
                message: i18n.t('Connections.modals.practiceAddedToast.body'),
                'aria-label': i18n.t('Connections.modals.practiceAddedToast.body'),
                icon: React.createElement(IconValidationValid, { color: "$support.positive.default" }),
            });
            mediator.publish('router:navigate', {
                url: '/lv-practice-connection',
                data: { practiceConnection: resp.practiceConnection },
            });
            return;
        }
        if (resp.errorCode === PracticeErrorCode.TOO_MANY_CONNECTIONS) {
            store.dispatch({
                type: SHOW_MODAL,
                modalProps: {
                    details: i18n.t('Connections.modals.maxPracticesError.body'),
                    testID: 'TooManyConnections',
                },
            });
            return;
        }
        showApiErrorModal();
    };
    const handleDecline = async () => {
        if (!practiceInvitation) {
            return;
        }
        setLoading(true);
        const resp = await acceptOrRejectPracticeInvitation(practiceInvitation.invitationId, 'decline');
        setLoading(false);
        if (!resp.errorCode) {
            showToast({
                message: i18n.t('Connections.modals.practiceRemovedToast.body'),
                'aria-label': i18n.t('Connections.modals.practiceRemovedToast.body'),
                icon: undefined,
            });
            mediator.publish('router:navigate', '/connections');
            return;
        }
        showApiErrorModal();
    };
    const onDeclineBtnClick = () => {
        store.dispatch({
            type: SHOW_MODAL,
            modalProps: {
                details: i18n.t('Connections.modals.declineInvitationDialog.body'),
                testID: 'DeclineInvitation',
                primaryAction: {
                    label: i18n.t('Global.microcopy.common.yes'),
                    onPress: () => {
                        handleDecline();
                        store.dispatch({ type: CLOSE_MODAL });
                    },
                },
                secondaryAction: {
                    label: i18n.t('Global.microcopy.common.no'),
                    onPress: () => store.dispatch({ type: CLOSE_MODAL }),
                },
            },
        });
    };
    if (!practiceInvitation) {
        mediator.publish('router:navigate', '/connections');
        return null;
    }
    return (React.createElement(Main, null,
        loading && React.createElement(Spinner, { "aria-label": "Loading", testID: "PendingPracticeInvitation.spinner" }),
        React.createElement(TitleActionBar, { testID: "PendingPracticeInvitation", title: i18n.t('Connections.content.pendingInvitation.primaryText') }),
        React.createElement(ScrollContainer, { marginTop: 0 },
            React.createElement(Form, null,
                React.createElement(Text, { testID: "PendingPracticeInvitation.secondaryText", fontSize: "$bodyBase.default", fontWeight: "$bodyBase.default", color: "$text.100", marginBottom: "$2" }, i18n.t('Connections.content.pendingInvitation.secondaryText')),
                React.createElement(Stack, { padding: "$4", backgroundColor: "$surface.container", border: "$1.5", shadow: "$1" },
                    React.createElement(Text, { testID: "PendingPracticeInvitation.practiceName", color: "$text.100", fontSize: "$h4.medium", fontWeight: "$bodyBase.strong" }, practiceInvitation.name),
                    React.createElement(VStack, { marginTop: "$6" },
                        React.createElement(Text, { testID: "PendingPracticeInvitation.practiceID.label", color: "$text.60", fontSize: "$bodySmall.default", fontWeight: "$bodySmall.default", marginBottom: "$0.5" }, i18n.t('Connections.content.pendingInvitation.formField.practiceID.label')),
                        React.createElement(Text, { testID: "PendingPracticeInvitation.practiceID", color: "$text.100", fontSize: "$bodyBase.medium", fontWeight: "$bodyBase.medium" }, practiceInvitation.businessId),
                        React.createElement(Divider, { marginVertical: "$4" }),
                        React.createElement(Text, { testID: "PendingPracticeInvitation.practiceAddress.label", color: "$text.60", fontSize: "$bodySmall.default", fontWeight: "$bodySmall.default", marginBottom: "$0.5" }, i18n.t('Connections.content.pendingInvitation.formField.practiceAddress.label')),
                        React.createElement(Text, { testID: "PendingPracticeInvitation.practiceAddress", color: "$text.100", fontSize: "$bodyBase.medium", fontWeight: "$bodyBase.medium" }, `${practiceInvitation.address1}, ${practiceInvitation.city}, ${practiceInvitation.state} ${practiceInvitation.zip}`),
                        React.createElement(Divider, { marginVertical: "$4" }),
                        React.createElement(Text, { testID: "PendingPracticeInvitation.practicePhoneNumber.label", color: "$text.60", fontSize: "$bodySmall.default", fontWeight: "$bodySmall.default", marginBottom: "$0.5" }, i18n.t('Connections.content.pendingInvitation.formField.practicePhoneNumber.label')),
                        React.createElement(Text, { testID: "PendingPracticeInvitation.practicePhoneNumber", color: "$text.100", fontSize: "$bodyBase.medium", fontWeight: "$bodyBase.medium" }, applyPhoneNumberMask(practiceInvitation.phoneNumber)))))),
        React.createElement(Footer, { buttonText: "", customProps: React.createElement(Stack, { padding: "$4" },
                React.createElement(RichText, { text: i18n.t('Connections.content.pendingInvitation.bodyText'), color: "$text.60", testID: "PendingPracticeInvitation.bodyText", onPressLink: onPrivacyNoticeLinkClick, fontToken: "$labelBase.default" }),
                React.createElement(HStack, { marginTop: "$4", justifyContent: "space-between" },
                    React.createElement(Button, { testID: "PendingPracticeInvitation.declineBtn", padding: "$4", marginRight: "$4", flexGrow: 1, onPress: onDeclineBtnClick, variant: "secondary", label: i18n.t('Global.microcopy.common.decline'), "aria-label": i18n.t('Global.microcopy.common.decline') }),
                    React.createElement(Button, { testID: "PendingPracticeInvitation.acceptBtn", flexGrow: 1, onPress: handleAccept, label: i18n.t('Global.microcopy.common.accept'), "aria-label": i18n.t('Global.microcopy.common.accept') }))) })));
};
export default PracticeInvitation;
